import { Injectable } from '@angular/core';
import { UtilsService } from '../helpers/utils.service';
import { environment } from '../../../environments/environment';
import { map, switchMap, of, catchError, throwError } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class AccessControlService {
  constructor(private utilsService: UtilsService) { }

  /** Base API URL sourced from environment configuration. */
  private baseUrl: string = environment.apiURL;
  /** Access-control service root endpoint. */
  private serviceUrl: string = this.baseUrl + '/accesscontrol';

  /**
   * Fetches account details for the currently authenticated user.
   */
  getAccountDetails() {
    return this.utilsService.httpGet(`${this.serviceUrl}/Account`);
  }

  /**
   * Fetches profile details for a specific user.
   * @param userId Numeric user identifier.
   */
  getUserDetails(userId: number) {
    return this.utilsService.httpGet(`${this.serviceUrl}/UserProfile/UserProfileView/${userId}`);
  }

  /**
   * Fetches current-user profile details by user id.
   * @param userId Numeric user identifier.
   */
  getCurrentUserDetails(userId: number) {
    return this.utilsService.httpGet(`${this.serviceUrl}/Account/UserProfile/${userId}`);
  }

  /**
   * Downloads a profile image/document blob by its public URL.
   * @param publicUrl Publicly resolvable document URL.
   */
  getImageData(publicUrl: string) {
    const url = `${this.baseUrl}/accesscontrol/Account/getDocument?url=${publicUrl}`;
    return this.utilsService.httpGetBlob(url);
  }

  /**
   * Requests access credentials or login redirect URL based on token exchange type.
   * @param token Authorization code or refresh token.
   * @param type Exchange type (for example, `CODE` or `REFRESHTOKEN`).
   */
  getAccessToken(token?: string, type?: string) {
    return this.utilsService.httpPostWithOptions(
      `${this.serviceUrl}/Token`,
      { token, type },
      { headers: { skip: 'true' } }
    );
  }

  /**
   * Fetches logout redirection URL from the backend.
   */
  getLogoutUrl() {
    return this.utilsService.httpGet(`${this.serviceUrl}/Account/logoutUrl/`);
  }

  /**
   * Fetches admin-level general settings.
   */
  getAdminSettings() {
    return this.utilsService.httpGet(`${this.serviceUrl}/GeneralSettings/adminSettings`);
  }

  /**
   * Updates dark-mode preference for the current scope.
   * @param isDarkMode Whether dark mode should be enabled.
   */
  postThemeUpdate(isDarkMode: boolean) {
    return this.utilsService.httpPost(
      `${this.serviceUrl}/GeneralSettings/generalSettings/darkMode/${isDarkMode}`,
      {}
    );
  }

  /**
   * Fetches contact persons for a customer.
   * @param customerId Numeric customer identifier.
   */
  getContactPersonList(customerId: number) {
    return this.utilsService.httpGet(`${this.serviceUrl}/Account/ContactPerson/${customerId}`);
  }

  /**
   * Fetches role list for a customer.
   * @param customerId Numeric customer identifier.
   */
  getRolesForCustomerId(customerId: number) {
    return this.utilsService.httpGet(`${this.serviceUrl}/EntityAccess/roles/${customerId}`);
  }

  /**
   * Creates or saves a user profile/account payload.
   * @param postData User profile creation payload.
   */
  saveUserProfile(postData: any) {
    const url = `${this.serviceUrl}/Account/userAccountCreation`;
    return this.utilsService.httpPost(url, postData);
  }

  /**
   * Updates customer profile picture using multipart form upload.
   * @param file Image file object to upload.
   * @param customerId Numeric customer identifier.
   */
  updateCustomerProfilePicture(file: any, customerId: number) {
    const formData = new FormData();
    formData.append('image', file);
    const url = `${this.serviceUrl}/UserProfile/customer/profilePicUpdate/${customerId}`;
    return this.utilsService.httpPost(url, formData);
  }

  /**
   * Fetches all users for a customer.
   * @param customerId Numeric customer identifier.
   */
  getUsersList(customerId: number) {
    return this.utilsService.httpGet(`${this.serviceUrl}/Account/${customerId}`);
  }

  /**
   * Fetches internal/super-admin role list.
   */
  getRolesForSuperAdmin() {
    return this.utilsService.httpGet(`${this.serviceUrl}/Role/LnTRoles`);
  }

  /**
   * Fetches role list for the provided customer.
   * @param customerId Numeric customer identifier.
   */
  getRoles(customerId: number) {
    return this.utilsService.httpGet(`${this.serviceUrl}/EntityAccess/roles/${customerId}`);
  }

  /**
   * Fetches entity master metadata for a customer.
   * @param customerId Numeric customer identifier.
   */
  getEntityMaster(customerId: number) {
    return this.utilsService.httpGet(`${this.serviceUrl}/EntityAccess/entityMaster/${customerId}`);
  }

  /**
   * Fetches entity-role mappings for a role.
   * @param roleId Numeric role identifier.
   */
  getEntityRoles(roleId: number) {
    return this.utilsService.httpGet(`${this.serviceUrl}/EntityAccess/entityRoles/${roleId}`);
  }

  /**
   * Saves role definition payload.
   * @param payload Role payload to persist.
   */
  saveRoles(payload: any) {
    return this.utilsService.httpPost(`${this.serviceUrl}/EntityAccess/roles`, payload);
  }

  /**
   * Deletes a role by id.
   * @param roleId Numeric role identifier.
   */
  deleteRole(roleId: number) {
    return this.utilsService.httpDelete(`${this.serviceUrl}/EntityAccess/roles/${roleId}`);
  }

  /**
   * Creates/updates a role and optionally refreshes role lists when API returns id `0`.
   * @param postData Role payload to create or update.
   * @param customerId Customer id for customer-specific role refresh. Pass `null` for super-admin refresh.
   */
  editAddRole(postData: any, customerId?: number | null) {
    const url = `${this.serviceUrl}/EntityAccess/roles`;
    return this.utilsService.httpPost(url, postData).pipe(
      map((successResponse) => {
        return successResponse;
      }),
      switchMap((successResponse) => {
        if (successResponse && successResponse.id === 0) {
          if (customerId === null) {
            return this.getRolesForSuperAdmin();
          } else {
            return this.getRolesForCustomerId(customerId as number);
          }
        } else {
          return of(successResponse);
        }
      }),
      catchError((error) => {
        return throwError(() => error.error);
      })
    );
  }

  /**
   * Saves entity access assignment payload.
   * @param payload Entity-role assignment payload.
   */
  saveEntityAccess(payload: any) {
    return this.utilsService.httpPost(`${this.serviceUrl}/EntityAccess/entityRole`, payload);
  }

  /**
   * Fetches user profile by id.
   * @param userId User identifier.
   */
  getUserProfile(userId: string) {
    return this.utilsService.httpGet(`${this.serviceUrl}/Account/UserProfile/${userId}`);
  }

  /**
   * Fetches EA-association user profile by id.
   * @param userId User identifier.
   */
  getEAUserProfile(userId: string) {
    return this.utilsService.httpGet(
      `${this.serviceUrl}/UserProfile/EACustomerAssociationUserProfiles/${userId}`
    );
  }

  /**
   * Deletes a user by id.
   * @param userId User identifier.
   */
  deleteUser(userId: string) {
    const url = `${this.serviceUrl}/Account/${userId}`;
    return this.utilsService.httpDelete(url);
  }

  /**
   * Updates active/inactive status for a user.
   * @param userId User identifier.
   * @param status Status flag to set.
   */
  updateStatus(userId: string, status: boolean) {
    const url = `${this.serviceUrl}/Account/UserProfileStatus/${userId}/${status}`;
    return this.utilsService.httpPut(url, null);
  }

  /**
   * Fetches users in E&A scope.
   */
  getUserListEAndA() {
    return this.utilsService.httpGet(`${this.serviceUrl}/Account/Users`);
  }

  /**
   * Creates a user profile in E&A scope.
   * @param postData User creation payload.
   */
  postUserProfile(postData: any) {
    const url = `${this.serviceUrl}/Account/userAccountCreation`;
    return this.utilsService.httpPost(url, postData);
  }

  /**
   * Updates a user profile.
   * @param putData User profile update payload.
   */
  updateUserProfile(putData: any) {
    const url = `${this.serviceUrl}/Account/userProfile`;
    return this.utilsService.httpPut(url, putData).pipe(
      map((successResponse) => {
        return successResponse;
      }),
      catchError((error) => {
        return throwError(() => error.error);
      })
    );
  }

  /**
   * Updates EA user profile details.
   * @param putData EA user update payload.
   */
  updateEAUserProfile(putData: any) {
    const url = `${this.serviceUrl}/Account/EAUserUpdate`;
    return this.utilsService.httpPut(url, putData);
  }

  /**
   * Invites a super-admin user.
   * @param postData Invitation payload.
   */
  inviteUser(postData: any) {
    const url = `${this.serviceUrl}/Account/superAdminUser`;
    return this.utilsService.httpPost(url, postData);
  }

  /**
   * Updates an invited super-admin user.
   * @param putData Update payload.
   */
  updateUser(putData: any) {
    const url = `${this.serviceUrl}/Account/superAdminUserUpdate`;
    return this.utilsService.httpPut(url, putData);
  }

  /**
   * Downloads customer-admin user template/report sheet.
   * @param id Numeric customer identifier.
   */
  downloadUserSheet(id: number) {
    const url = `${this.serviceUrl}/Account/generate/customerAdminUser/${id}`;
    return this.utilsService.httpGetBlob(url);
  }

  /**
   * Uploads customer-admin user sheet.
   * @param files File input array where first item is uploaded.
   * @param id Numeric customer identifier.
   */
  uploadUserSheet(files: any, id: number) {
    const formData = new FormData();
    formData.append('file', files[0]);
    const url = `${this.serviceUrl}/Account/upload/customerAdminUser/${id}`;
    return this.utilsService.httpPost(url, formData);
  }

  /**
   * Downloads super-admin invited users sheet.
   */
  downloadSuperAdminUserSheet() {
    const url = `${this.serviceUrl}/Account/generate/internalInvitedUsers`;
    return this.utilsService.httpGetBlob(url);
  }

  /**
   * Uploads internal invited users sheet.
   * @param files File input array where first item is uploaded.
   */
  uploadSuperAdminUserSheet(files: any) {
    const formData = new FormData();
    formData.append('file', files[0]);
    const url = `${this.serviceUrl}/Account/upload/InvitedInternalUsers`;
    return this.utilsService.httpPost(url, formData);
  }

  /**
   * Fetches all roles available in the system.
   */
  getAllRoles() {
    const url = `${this.serviceUrl}/Role`;
    return this.utilsService.httpGet(url);
  }

  /**
   * Saves customer general settings.
   * @param payload Settings payload.
   * @param customerId Numeric customer identifier.
   */
  saveGeneralSettings(payload: any, customerId: number) {
    return this.utilsService.httpPost(
      `${this.serviceUrl}/GeneralSettings/generalSettings/${customerId}`,
      payload
    );
  }

  /**
   * Updates push notification preference for a user.
   * @param userId Numeric user identifier.
   * @param isEnabled Notification state to set.
   */
  updatePushNotification(userId: number, isEnabled: boolean) {
    return this.utilsService.httpPatch(
      `${this.serviceUrl}/GeneralSettings/pushNotificationUpdate/${userId}/${isEnabled}`,
      isEnabled
    );
  }

  /**
   * Updates dark-mode view preference.
   * @param isDarkMode Whether dark mode should be enabled.
   */
  updateViewPreference(isDarkMode: boolean) {
    return this.utilsService.httpPatch(
      `${this.serviceUrl}/GeneralSettings/generalSettings/darkMode/${isDarkMode}`,
      isDarkMode
    );
  }

  /**
   * Deletes user profile picture.
   * @param userId Numeric user identifier.
   */
  deleteUserProfilePicture(userId: number) {
    const url = `${this.baseUrl}/accesscontrol/UserProfile/deleteProfilePic/${userId}`;
    return this.utilsService.httpDelete(url);
  }

  /**
   * Updates user profile picture using multipart form upload.
   * @param file Image file object to upload.
   * @param userId Numeric user identifier.
   */
  updateUserProfilePicture(file: any, userId: number) {
    const formData = new FormData();
    formData.append('image', file);
    const url = `${this.baseUrl}/accesscontrol/UserProfile/profilePicUpdate/${userId}`;
    return this.utilsService.httpPost(url, formData);
  }

  /**
   * Builds notification-master endpoint URL.
   */
  notificationMaster = () => `${this.serviceUrl}/GeneralSettings/notificationMaster`;

  /**
   * Builds customer-specific notification endpoint URL.
   * @param customerId Numeric customer identifier.
   */
  notificationData = (customerId: number) =>
    `${this.serviceUrl}/GeneralSettings/notification/${customerId}`;

  /**
   * Builds user-specific push-notification status endpoint URL.
   * @param userId Numeric user identifier.
   */
  pushNotificationStatus = (userId: number) =>
    `${this.serviceUrl}/GeneralSettings/pushNotification/${userId}`;

  /**
   * Builds customer measurement units endpoint URL.
   * @param customerId Numeric customer identifier.
   */
  unitData = (customerId: number) =>
    `${this.serviceUrl}/GeneralSettings/measurementUnits/${customerId}`;

  /**
   * Checks whether an email can be used for account creation.
   * @param email Email address to validate.
   */
  checkMailtoUse(email: string) {
    const url = `${this.baseUrl}/accesscontrol/Account/${email}/canuse`;
    return this.utilsService.httpPost(url, {});
  }

  /**
   * Generates OTP for a mobile number and optional user context.
   * @param mobileNumber Target mobile number.
   * @param traceId Client trace id used to correlate OTP lifecycle.
   * @param userId Optional user id for profile-update OTP flow.
   */
  generateOTP(mobileNumber: number, traceId: string, userId?: number) {
    const url =
      typeof userId !== 'undefined'
        ? `${this.serviceUrl}/Otp/generate?userId=${userId}`
        : `${this.serviceUrl}/Otp/generate`;

    return this.utilsService.httpPost(url, {
      mobileNumber,
      traceId,
      otpValue: '',
    });
  }

  /**
   * Verifies OTP against mobile number and trace id.
   * @param otp One-time password value.
   * @param mobileNumber Target mobile number.
   * @param traceId Trace id used during OTP generation.
   */
  verifyMobileOTP(otp: number, mobileNumber: number, traceId: string) {
    return this.utilsService.httpPost(`${this.serviceUrl}/Otp/verify`, {
      mobileNumber,
      traceId,
      otpValue: otp,
    });
  }

  /**
   * Registers a new account.
   * @param formData Account registration payload.
   */
  registerAccount(formData: any) {
    return this.utilsService.httpPost(`${this.serviceUrl}/Account`, formData).pipe(
      map((response) => response),
      catchError((error) => throwError(() => error?.error ?? error))
    );
  }

  /**
   * Fetches countries list for registration dropdown.
   */
  getCountries() {
    return this.utilsService.httpGet(`${this.serviceUrl}/Country/all/`);
  }

  /**
   * Fetches privacy notice URL/content reference.
   */
  getPrivacyNotice() {
    return this.utilsService.httpGet(`${this.serviceUrl}/Account/privacyNoticeUrl`);
  }

  /**
   * Fetches privacy policy URL/content reference.
   */
  getPrivacyPolicy() {
    return this.utilsService.httpGet(`${this.serviceUrl}/Account/privacyPolicyUrl`);
  }

  /**
   * Fetches terms and conditions and maps it to legacy register shape.
   */
  getTermsAndConditions() {
    return this.utilsService
      .httpGet(`${this.serviceUrl}/Account/GetTermsAndCondition`)
      .pipe(map((data) => ({ termsAndConditions: data })));
  }

  /**
   * Fetches countries list for profile forms.
   */
  getCountry() {
    return this.utilsService.httpGet(`${this.serviceUrl}/Country/all`);
  }

  /**
   * Fetches available business types.
   */
  getBusinessType() {
    return this.utilsService.httpGet(`${this.serviceUrl}/UserProfile/BussinessTypes`);
  }

  /**
   * Fetches profile details by user id.
   * @param userId Numeric user identifier.
   */
  getUserProfileDetails(userId: number) {
    return this.utilsService.httpGet(`${this.serviceUrl}/UserProfile/UserProfileView/${userId}`);
  }

  /**
   * Updates user profile details.
   * @param body User profile payload.
   */
  addUserProfileDetails(body: any) {
    return this.utilsService.httpPut(`${this.serviceUrl}/UserProfile/update`, body).pipe(
      catchError((error) => of(error))
    );
  }

  /**
   * Fetches support request type options used by contact/support forms.
   * @returns Observable containing available contact request types.
   */
  getRequestTypes() {
    return this.utilsService.httpGet(
      `${this.baseUrl}/accesscontrol/UserProfile/contactReqTypes`
    );
  }

  /**
   * Submits a customer support/contact request.
   * @param payload Support request payload from the contact form.
   * @returns Observable containing submit operation response.
   */
  submitContactRequest(payload: any) {
    const url = `${this.baseUrl}/accesscontrol/UserProfile/SupportRequest`;
    return this.utilsService.httpPost(url, payload);
  }

  /**
   * Updates user agreement status for terms and conditions.
   * @param agreeTerms  Boolean indicating whether the user agrees to the terms and conditions.
   * @returns Observable containing the update operation response.
   */
  updateTermsAgreement(agreeTerms: boolean) {
    const url = `${this.baseUrl}/accesscontrol/Account/UpdateAgreement`
    return this.utilsService.httpPatch(url, agreeTerms);
  }
}
